import { useState, useEffect, useRef } from 'react'
import { Outlet, Link, useLocation } from '@tanstack/react-router'
import { useLang } from '@/hooks/useLang'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Menu, X } from 'lucide-react'
import { allDocs } from 'content-collections'

interface Heading {
  id: string
  text: string
  level: number
}

type NavItem =
  | { title: string; routeTo: '/$lang/docs/'; toParam: string }
  | { title: string; routeTo: '/$lang/docs/$docSlug'; docSlug: string; toParam: string }
  | { title: string; routeTo: '/$lang/docs/api-reference'; toParam: string }

interface NavGroup {
  title: string
  items: NavItem[]
}

const SECTION_ORDER = ['Getting Started', 'Guides', 'Reference']

function getNavGroups(lang: string): NavGroup[] {
  const bySection = new Map<string, NavItem[]>()

  const sorted = [...allDocs].sort((a, b) => a.order - b.order)
  for (const doc of sorted) {
    const isIndex = doc.slug === 'introduction'
    const item: NavItem = isIndex
      ? { title: doc.title, routeTo: '/$lang/docs/', toParam: `/${lang}/docs` }
      : { title: doc.title, routeTo: '/$lang/docs/$docSlug', docSlug: doc.slug, toParam: `/${lang}/docs/${doc.slug}` }
    if (!bySection.has(doc.section)) bySection.set(doc.section, [])
    bySection.get(doc.section)!.push(item)
  }

  const groups: NavGroup[] = []
  for (const section of SECTION_ORDER) {
    if (bySection.has(section)) {
      groups.push({ title: section, items: bySection.get(section)! })
    }
  }

  // API Reference is a dynamic OpenAPI renderer, not a markdown doc
  const refGroup = groups.find((g) => g.title === 'Reference')
  const apiRefItem: NavItem = {
    title: 'API Reference',
    routeTo: '/$lang/docs/api-reference',
    toParam: `/${lang}/docs/api-reference`,
  }
  if (refGroup) {
    refGroup.items.push(apiRefItem)
  } else {
    groups.push({ title: 'Reference', items: [apiRefItem] })
  }

  return groups
}

function SidebarNav({ lang, currentPath, onNavigate }: { lang: string; currentPath: string; onNavigate?: () => void }) {
  const groups = getNavGroups(lang)
  const normalizedCurrent = currentPath.replace(/\/$/, '')

  return (
    <nav className="py-6 px-3">
      {groups.map((group) => (
        <div key={group.title} className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 px-2">
            {group.title}
          </p>
          <ul className="space-y-0.5">
            {group.items.map((item) => {
              const active = normalizedCurrent === item.toParam
              const linkProps =
                item.routeTo === '/$lang/docs/$docSlug'
                  ? { to: item.routeTo, params: { lang, docSlug: item.docSlug } }
                  : { to: item.routeTo, params: { lang } }
              return (
                <li key={item.toParam}>
                  <Link
                    {...(linkProps as any)}
                    onClick={onNavigate}
                    className={cn(
                      'block px-3 py-1.5 text-sm rounded-md transition-colors',
                      active
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    )}
                  >
                    {item.title}
                  </Link>
                </li>
              )
            })}
          </ul>
        </div>
      ))}
    </nav>
  )
}

export function DocsLayout() {
  const lang = useLang()
  const location = useLocation()
  const [headings, setHeadings] = useState<Heading[]>([])
  const [mobileOpen, setMobileOpen] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)

  // Close mobile sidebar on navigation
  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  // Auto-detect headings for on-page nav after render
  useEffect(() => {
    const timer = setTimeout(() => {
      const el = contentRef.current
      if (!el) return
      const nodes = el.querySelectorAll('h2[id], h3[id]')
      const items: Heading[] = Array.from(nodes).map((node) => ({
        id: node.getAttribute('id') ?? '',
        text: node.textContent ?? '',
        level: parseInt(node.tagName[1], 10),
      }))
      setHeadings(items)
    }, 50)
    return () => clearTimeout(timer)
  }, [location.pathname])

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-16 z-30 bg-background/95 backdrop-blur border-b px-4 py-2 flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          className="gap-2 h-8 text-muted-foreground"
          onClick={() => setMobileOpen(true)}
        >
          <Menu className="h-4 w-4" />
          <span className="text-sm">Docs</span>
        </Button>
      </div>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileOpen(false)}
          />
          <div className="relative z-10 w-72 bg-background h-full overflow-y-auto shadow-xl">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <span className="font-semibold text-sm">Documentation</span>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setMobileOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <SidebarNav
              lang={lang}
              currentPath={location.pathname}
              onNavigate={() => setMobileOpen(false)}
            />
          </div>
        </div>
      )}

      <div className="max-w-screen-xl mx-auto lg:flex">
        {/* Desktop left sidebar */}
        <aside className="hidden lg:block w-56 xl:w-60 shrink-0 sticky top-16 self-start h-[calc(100vh-4rem)] overflow-y-auto border-r border-border/50">
          <SidebarNav lang={lang} currentPath={location.pathname} />
        </aside>

        {/* Main content */}
        <main
          ref={contentRef}
          className="flex-1 min-w-0 px-4 sm:px-6 lg:px-10 xl:px-14 py-10 pb-20"
        >
          <Outlet />
        </main>

        {/* Desktop right on-page nav */}
        <aside className="hidden xl:block w-52 shrink-0 sticky top-16 self-start h-[calc(100vh-4rem)] overflow-y-auto py-10 pr-4">
          {headings.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                On this page
              </p>
              <ul className="space-y-1.5 border-l border-border/50 pl-3">
                {headings.map((h) => (
                  <li key={h.id}>
                    <a
                      href={`#${h.id}`}
                      className={cn(
                        'block text-sm text-muted-foreground hover:text-foreground transition-colors leading-snug',
                        h.level === 3 && 'pl-3 text-xs'
                      )}
                    >
                      {h.text}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </div>
    </div>
  )
}
